/**
 * Environment-aware debug logging utility
 * Logs are only shown in development mode to keep production clean
 */
import { captureException } from '@/sentry';

const isDev = import.meta.env.DEV;
const isProd = import.meta.env.MODE === 'production';

export const debug = {
    /**
     * Log informational messages (only in development)
     */
    log: (...args: unknown[]): void => {
        if (isDev) console.log(...args);
    },

    /**
     * Log error messages (only in development)
     */
    error: (...args: unknown[]): void => {
        if (isDev) console.error(...args);
    },

    /**
     * Log warning messages (only in development)
     */
    warn: (...args: unknown[]): void => {
        if (isDev) console.warn(...args);
    },

    /**
     * Capture critical errors (Sentry in production, console in development)
     */
    captureError: (error: unknown, context?: string): void => {
        if (isDev) {
            const prefix = context ? `[${context}]` : '[Error]';
            console.error(prefix, error);
            return;
        }

        if (isProd) {
            if (error instanceof Error) {
                captureException(error);
            } else {
                captureException(new Error(context ? `${context}: ${String(error)}` : String(error)));
            }
        }
    },

    /**
     * Log messages with a specific prefix/context
     */
    context: (context: string) => ({
        log: (...args: unknown[]): void => {
            if (isDev) console.log(`[${context}]`, ...args);
        },
        error: (...args: unknown[]): void => {
            if (isDev) console.error(`[${context}]`, ...args);
        },
        warn: (...args: unknown[]): void => {
            if (isDev) console.warn(`[${context}]`, ...args);
        },
        captureError: (error: unknown): void => {
            debug.captureError(error, context);
        },
    }),
};

// Pre-configured loggers for common contexts
export const authLogger = debug.context('Auth');
export const calendarLogger = debug.context('Calendar');
export const taskLogger = debug.context('Task');
export const clientLogger = debug.context('Client');
export const profileLogger = debug.context('Profile');
export const serviceLogger = debug.context('Service');
